import {
  Badge,
  Box,
  Button,
  Flex,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { brandPalette } from "../../../constants/brandPalette";
import React, { useEffect, useMemo, useState } from "react";
import ReactSelect from "react-select";
import { useDispatch, useSelector } from "react-redux";
import Layout from "../../../components/dashboard/Layout";
import LogoutMenu from "../../../components/dashboard/Layout/logout-menu";
import { adminModalContentProps, adminSelectStyles } from "../../../components/Models/admin-modal-theme";
import { detailedExamReportRequest } from "../../../modules/admin-modules/reports/Actions";
import { getAllExamsRequest } from "../../../modules/admin-modules/exams/Actions";
import { API } from "../../../constants";

const examSelectBase = adminSelectStyles({ menuZIndex: 1600 });

const selectStyles = {
  ...examSelectBase,
  menuPortal: (base) => ({ ...base, zIndex: 1600 }),
  control: (styles, state) => ({
    ...examSelectBase.control(styles, state),
    borderRadius: "14px",
    minHeight: "44px",
    minWidth: "260px",
  }),
};

function stripQuestionPreview(item) {
  const nested = item?.question;
  const raw =
    (typeof nested === "string" && nested) ||
    (nested && typeof nested.question === "string" && nested.question) ||
    (typeof item?.question_text === "string" && item.question_text) ||
    "";
  const text = String(raw)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!text) return "—";
  return text.length > 200 ? `${text.slice(0, 197)}…` : text;
}

function chapterLabel(item) {
  const ch = item?.chapter ?? item?.question?.chapter;
  if (!ch) return "—";
  if (typeof ch === "string") return ch;
  return ch.name ?? ch.title ?? "—";
}

/** Relative paths are joined to API origin (without trailing `/api`). */
function resolveQuestionImageUrl(raw) {
  if (!raw || typeof raw !== "string") return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const base = String(API || "")
    .replace(/\/?api\/?$/i, "")
    .replace(/\/$/, "");
  const origin = base || (typeof window !== "undefined" ? window.location.origin : "");
  const path = trimmed.replace(/^\//, "");
  return `${origin}/${path}`;
}

function getQuestionImageSrc(item) {
  const img = item?.question?.image;
  return resolveQuestionImageUrl(img);
}

const DetailedExamReport = () => {
  const dispatch = useDispatch();
  const [examId, setExamId] = useState("");
  const [questionImageModal, setQuestionImageModal] = useState({ open: false, src: "", title: "" });

  const exams = useSelector((state) => state.exams.exams);
  const detailed = useSelector((state) => state.reports.detailedExamReport);
  const loading = useSelector((state) => state.reports.isLoading);

  useEffect(() => {
    dispatch(getAllExamsRequest());
  }, [dispatch]);

  useEffect(() => {
    if (!examId) return;
    dispatch(detailedExamReportRequest(examId));
  }, [examId, dispatch]);

  const examOptions = useMemo(() => {
    const raw = Array.isArray(exams) ? exams : [];
    return raw.map((e) => ({
      value: String(e.id),
      label: e.name || `Exam #${e.id}`,
    }));
  }, [exams]);

  const students = detailed?.students ?? [];
  const missed = detailed?.top_missed_questions ?? [];

  const studentRows = useMemo(() => {
    if (!examId || !Array.isArray(students)) return [];
    return students.map((row, index) => {
      const examRel = row?.exam?.find((val) => String(val?.id) === String(examId));
      const pivot = examRel?.pivot;
      const right = pivot?.right_answers;
      const total =
        examRel?.question_number ??
        pivot?.question_number ??
        examRel?.pivot?.question_number;
      const grade =
        right != null && total != null ? `${right} / ${total}` : "—";
      return {
        key: row?.id ?? index,
        index: index + 1,
        name: row?.name ?? "—",
        group: row?.group?.name ?? "—",
        grade,
        phone: row?.phone ?? "—",
        parent: row?.parent_pone ?? row?.parent_phone ?? "—",
      };
    });
  }, [students, examId]);

  return (
    <Layout>
      <Box
        minH="400px"
        maxW="1440px"
        mx="auto"
        w="100%"
        px={{ base: 4, sm: 5, md: 8, lg: 10 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 10, md: 12 }}
      >
        <Flex
          w="100%"
          mb={6}
          justifyContent="space-between"
          alignItems={{ base: "stretch", md: "flex-start" }}
          gap={4}
          flexWrap="wrap"
        >
          <Box flex="1" minW="0">
            <Text fontSize={{ base: "18px", md: "24px" }} fontWeight="900" color="var(--text-primary)">
              Detailed exam report
            </Text>
            <Text mt={1} fontSize="sm" fontWeight="700" color="var(--text-muted)">
              Student scores for the selected exam, plus the questions most often answered incorrectly
              (after correction).
            </Text>
          </Box>
          <Flex align="flex-start" gap={3} flexWrap="wrap" justifyContent={{ base: "flex-start", md: "flex-end" }}>
            <Box minW={{ base: "100%", md: "280px" }} maxW={{ base: "100%", md: "420px" }}>
              <ReactSelect
                options={examOptions}
                isLoading={loading && !detailed}
                placeholder="Select exam"
                styles={selectStyles}
                value={examOptions.find((o) => o.value === String(examId)) || null}
                onChange={(opt) => setExamId(opt?.value ? String(opt.value) : "")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPosition="fixed"
                isClearable
              />
            </Box>
            <LogoutMenu />
          </Flex>
        </Flex>

        {!examId ? (
          <Text fontWeight="600" color="var(--text-muted)">
            Choose an exam to load results and missed questions.
          </Text>
        ) : loading && (!detailed || (students.length === 0 && missed.length === 0)) ? (
          <Text fontWeight="600" color="var(--text-primary)">
            Loading report…
          </Text>
        ) : (
          <>
            <Box
              border="1px solid"
              borderColor="var(--border-subtle)"
              bg="var(--surface-raised)"
              borderRadius="18px"
              p={{ base: 4, md: 6 }}
              mb={8}
              boxShadow="0 18px 55px rgba(0,0,0,0.12)"
              overflowX="auto"
              color="var(--text-primary)"
            >
              <Text fontSize="md" fontWeight="800" color="var(--text-primary)" mb={4}>
                Students & grades
              </Text>
              {studentRows.length === 0 ? (
                <Text fontWeight="600" color="var(--text-muted)">
                  No student rows returned for this exam.
                </Text>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th color="var(--text-primary)">#</Th>
                      <Th color="var(--text-primary)">Student</Th>
                      <Th color="var(--text-primary)">Group</Th>
                      <Th color="var(--text-primary)">Score</Th>
                      <Th color="var(--text-primary)">Phone</Th>
                      <Th color="var(--text-primary)">Parent</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {studentRows.map((r) => (
                      <Tr key={r.key}>
                        <Td fontWeight="700">#{r.index}</Td>
                        <Td fontWeight="600">{r.name}</Td>
                        <Td>{r.group}</Td>
                        <Td fontWeight="800">{r.grade}</Td>
                        <Td>{r.phone}</Td>
                        <Td>{r.parent}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              )}
            </Box>

            <Box
              border="1px solid"
              borderColor="var(--border-subtle)"
              bg="var(--surface-raised)"
              borderRadius="18px"
              p={{ base: 4, md: 6 }}
              boxShadow="0 18px 55px rgba(0,0,0,0.12)"
              overflowX="auto"
              color="var(--text-primary)"
            >
              <Flex align="baseline" gap={3} mb={4} flexWrap="wrap">
                <Text fontSize="md" fontWeight="800" color="var(--text-primary)">
                  Top missed questions
                </Text>
                <Badge colorScheme="red" borderRadius="md" fontWeight="700">
                  {missed.length} ranked
                </Badge>
              </Flex>
              {missed.length === 0 ? (
                <Text fontWeight="600" color="var(--text-muted)">
                  No missed-question aggregates for this exam (or corrections not recorded yet).
                </Text>
              ) : (
                <Table size="sm" variant="simple">
                  <Thead>
                    <Tr>
                      <Th color="var(--text-primary)">#</Th>
                      <Th color="var(--text-primary)">Times wrong</Th>
                      <Th color="var(--text-primary)">Chapter</Th>
                      <Th color="var(--text-primary)">Section</Th>
                      <Th color="var(--text-primary)">Question</Th>
                      <Th color="var(--text-primary)">Show question image</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {missed.map((item, idx) => {
                      const imgSrc = getQuestionImageSrc(item);
                      return (
                      <Tr key={item?.id ?? `${item?.question_id}-${idx}`}>
                        <Td fontWeight="700">#{idx + 1}</Td>
                        <Td fontWeight="800">{item?.wrong_count ?? item?.count ?? "—"}</Td>
                        <Td maxW="200px">{chapterLabel(item)}</Td>
                        <Td maxW="160px" fontSize="sm">
                          {item?.section ?? "—"}
                        </Td>
                        <Td color="var(--text-primary)" fontSize="sm" whiteSpace="normal">
                          {stripQuestionPreview(item)}
                        </Td>
                        <Td verticalAlign="middle">
                          {imgSrc ? (
                            <Button
                              size="sm"
                              borderRadius="lg"
                              variant="outline"
                              borderColor={brandPalette.primary}
                              color={brandPalette.primary}
                              fontWeight="700"
                              _hover={{ bg: "rgba(91, 13, 22, 0.12)" }}
                              onClick={() =>
                                setQuestionImageModal({
                                  open: true,
                                  src: imgSrc,
                                  title: `Question #${idx + 1}`,
                                })
                              }
                            >
                              Show image
                            </Button>
                          ) : (
                            <Text fontSize="sm" color="var(--text-muted)" fontWeight="600">
                              —
                            </Text>
                          )}
                        </Td>
                      </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              )}
            </Box>
          </>
        )}
      </Box>

      <Modal
        isOpen={questionImageModal.open}
        onClose={() => setQuestionImageModal((s) => ({ ...s, open: false }))}
        size="4xl"
        isCentered
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          {...adminModalContentProps}
          mx={4}
          border="1px solid"
          borderColor="var(--border-subtle)"
          color="var(--text-primary)"
        >
          <ModalHeader fontWeight="800" pb={2} color="var(--text-primary)" borderBottomWidth={0}>
            {questionImageModal.title || "Question image"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={8}>
            {questionImageModal.src ? (
              <Image
                src={questionImageModal.src}
                alt="Question"
                maxW="100%"
                height="auto"
                borderRadius="lg"
                border="1px solid"
                borderColor="var(--border-subtle)"
                objectFit="contain"
              />
            ) : null}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Layout>
  );
};

export default DetailedExamReport;
